import {
  useApiListingParams,
  usePermissions,
  useShallowQueryParams
} from '$hooks/actions'
import { trueOrUndefined } from '@genie-fintech/ui/functions'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  container,
  desc,
  grid,
  subHeader,
  headingWrap,
  title,
  searchFilterWrap
} from './styles.css'
import { flexColumn, ready } from '$styles/common.css'
import SearchBox from '$components/SearchBox'
import Loading from '$components/Loading'
import ScrollPagination from '$components/ScrollPagination'
import AppCard from '$components/AppCard'
import { PERMISSIONS } from '$app/constants/common'
import Empty from '$components/Empty'
import { useRequest, useUnmount } from 'ahooks'
import { APPS_RESPONSE, getApplications } from '$services/apps'
import useResponsive from '$hooks/actions/useResponsive'
import usePageClass from '$components/Modal/hooks/usePageClass'
import ScrollTopButton from '$components/ScrollTopButton'
import { useAppListStore } from '$hooks/stores'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { Button } from '@genie-fintech/ui/components'
import { Plus } from 'lucide-react'
import { redirect, ROUTE_NAMES } from '$router/config'
import { COMPANY_NAME } from '$constants/themeTexts'

const AppList = () => {
  const responsive = useResponsive()

  const { route } = useRouteSummary()
  const { tenantId = '' } = route.params
  const { q = '' } = route.queryParams

  const { updateShallowQueryParams } = useShallowQueryParams()

  const [apps, setApps] = useState<APPS_RESPONSE['data']>([])

  const isMounted = useIsMounted()

  usePageClass({ prefix: 'background-low', name: 'app-list-page' })

  const storedSearchValue = useAppListStore(state => state.searchValue)
  const updateStoredSearchValue = useAppListStore(state => state.setSearchValue)
  const resetStoredSearchValue = useAppListStore(
    state => state.resetSearchValue
  )

  const { checkPermissions } = usePermissions()
  const CAN_CREATE_APP = checkPermissions([PERMISSIONS.APPLICATION_CREATE])

  const perPage: number = useMemo(() => {
    if (responsive.smallDesktop) return 25
    return 24
  }, [responsive.smallDesktop])

  const { runAsync, loading, data } = useRequest(getApplications, {
    manual: true,
    onSuccess: ({ data, meta: { current_page } }) => {
      setApps(prev => (current_page === 1 ? data : [...prev, ...data]))
    }
  })

  const { meta } = data ?? {}

  const { updateSearchValue, searchValue, debouncedSearchValue, pagerProps } =
    useApiListingParams({
      total: meta?.total,
      perPage,
      initialSearchValue: storedSearchValue || q
    })

  const { currentPage, jump } = pagerProps

  useEffect(() => {
    if (!tenantId) return

    setApps([])
    jump(1)
  }, [tenantId, jump])

  useEffect(() => {
    if (!tenantId) return

    runAsync({
      page: currentPage,
      per_page: perPage,
      q: debouncedSearchValue
    })
  }, [runAsync, currentPage, perPage, debouncedSearchValue, tenantId])

  const initialLoading = currentPage === 1 && loading

  const isEmpty = !apps.length && !loading

  const totalApps = meta?.total

  useEffect(() => {
    updateShallowQueryParams({ q: debouncedSearchValue })
    updateStoredSearchValue(debouncedSearchValue)
  }, [debouncedSearchValue, updateShallowQueryParams, updateStoredSearchValue])

  useUnmount(() => {
    const nextRoute = window.location.pathname.split('/')[2]
    if (route.name !== nextRoute) {
      resetStoredSearchValue()
    }
  })

  const onCreateApp = useCallback(() => {
    redirect(ROUTE_NAMES.CREATE_APP, options => options)
  }, [])

  return (
    <section className={container} data-ready={trueOrUndefined(isMounted)}>
      <header className={flexColumn} style={{ gap: 20 }}>
        <div>
          <div className={headingWrap}>
            <h2 className={title}>Applications</h2>
            <span className={ready} data-ready={trueOrUndefined(totalApps)}>
              -
            </span>
            <span className={ready} data-ready={trueOrUndefined(totalApps)}>
              {totalApps}
            </span>
          </div>
          <p className={desc}>
            Setup a mobile, web application to use {COMPANY_NAME} for
            Authentication.
          </p>
        </div>
        <div className={subHeader}>
          <div className={searchFilterWrap}>
            <SearchBox
              value={searchValue}
              onChange={updateSearchValue}
              placeholder="Search here..."
            />
          </div>

          {CAN_CREATE_APP && (
            <Button onClick={onCreateApp}>
              <Plus size={16} />
              Create Application
            </Button>
          )}
        </div>
      </header>

      <main className={flexColumn} style={{ flex: 1 }}>
        {initialLoading && <Loading />}

        {isEmpty && (
          <Empty
            title="You don't have any application yet."
            description="No Items have been added to this section"
            buttonProps={
              CAN_CREATE_APP
                ? {
                    label: 'Create Application',
                    onClick: onCreateApp
                  }
                : undefined
            }
          />
        )}

        <article className={grid}>
          {apps.map(appData => (
            <AppCard key={appData.id} data={appData} />
          ))}
        </article>

        <ScrollPagination loading={loading} meta={meta} onTrigger={jump} />
      </main>

      {!responsive.mediumTablet && <ScrollTopButton />}
    </section>
  )
}

export default AppList
